import React, { useState } from "react";
import {DataGrid} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  IconButton,
  AppBar,
  Card,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import properties from "constants/ApplicationConstants";
import DescriptionIcon from "@mui/icons-material/Description";
import { useNavigate } from "react-router-dom";

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        px: 1,
        py: 0.5,
        borderTop: "1px solid #e0e0e0",
        gap: 1,
        flexWrap: "nowrap"
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No records"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ mr: 1, color: "#666" }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={() => onPageChange(0)} disabled={page === 0 || loading} size="small">
            <FirstPage fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous">
          <IconButton
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next">
          <IconButton
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={onGoToLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": { backgroundColor: "#1565c0" },
              "&.Mui-disabled": { backgroundColor: "#e0e0e0", color: "#9e9e9e" }
            }}
          >
            <LastPage fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function KRAStatus() {
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize] = React.useState(20);
  const [searchEmpId, setSearchEmpId] = React.useState("");
  const [employeeStatus, setEmployeeStatus] = React.useState("all");
  const [managerStatus, setManagerStatus] = React.useState("all");
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });

  const managerId = localStorage.getItem("empId") || "14330";

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const fetchEmployeesWithKRA = async () => {
    setLoading(true);
    try {
      const response = await GET(`manager/reportees/${managerId}`);

      if (response?.status === 200 && response?.data?.data) {
        const employeesData = response.data.data;

        const employees = Array.isArray(employeesData)
          ? employeesData.map((emp, index) => ({
              id: index + 1,
              empId: emp.empId,
              appraisalId: emp.appraisalId,
              fullName: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
              designation: emp.designation?.replace(/_/g, " ").toUpperCase() || "-",
              department: emp.department || "-",
              hasKRA: emp.applicableSkills && emp.applicableSkills.length > 0,
              empPermission: emp.empPermission,
              managerPermission: emp.managerPermission,
              status: emp.status
            }))
          : [];

        const employeesWithKRA = employees.filter(emp => emp.hasKRA);
        setRows(employeesWithKRA);
        setFilteredRows(employeesWithKRA);

        if (employeesWithKRA.length === 0) {
          showSnackbar("No employees have KRA assigned yet", "info");
        }
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to load employees", "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchEmployeesWithKRA();
  }, []);

  const handleSearch = () => {
    let filtered = rows;

    if (searchEmpId.trim()) {
      filtered = filtered.filter(row =>
        row.empId.toString().includes(searchEmpId.trim())
      );
    }

    if (employeeStatus !== "all") {
      filtered = filtered.filter(row =>
        employeeStatus === "pending" ? row.empPermission === 1 : row.empPermission !== 1
      );
    }

    if (managerStatus !== "all") {
      filtered = filtered.filter(row =>
        managerStatus === "pending" ? row.managerPermission === 1 : row.managerPermission !== 1
      );
    }

    setFilteredRows(filtered);
    setPage(0);

    if (filtered.length === 0) {
      showSnackbar("No matching records found", "info");
    } else {
      showSnackbar(`Found ${filtered.length} record(s)`, "success");
    }
  };

  const handleReset = () => {
    setSearchEmpId("");
    setEmployeeStatus("all");
    setManagerStatus("all");
    setFilteredRows(rows);
    setPage(0);
  };

  const handlePageChange = newPage => setPage(newPage);

  const handleGoToLastPage = () => {
    const totalPages = Math.ceil(filteredRows.length / pageSize);
    if (totalPages > 0) setPage(totalPages - 1);
  };

  const StatusChip = ({ label, color }) => (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        fontWeight: "500",
        minWidth: "80px",
        color: "#fff",
        fontFamily: properties.fontFamily,
        height: "22px",
        fontSize: "12px"
      }}
    />
  );

  const columns = [
    { field: "id", headerName: "#", width: 55, align: "center", headerAlign: "center" },
    { field: "empId", headerName: "EMP ID", width: 95 },
    { field: "fullName", headerName: "Employee Name", width: 200 },
    { field: "designation", headerName: "Designation", width: 175 },
    {
      field: "empPermission",
      headerName: "Employee Rating",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <StatusChip
          label={params.row.empPermission === 1 ? "Pending" : "Completed"}
          color={params.row.empPermission === 1 ? "error" : "success"}
        />
      )
    },
    {
      field: "managerPermission",
      headerName: "Manager Rating",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: params => (
        <StatusChip
          label={params.row.managerPermission === 1 ? "Pending" : "Completed"}
          color={params.row.managerPermission === 1 ? "error" : "success"}
        />
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: params => (
        <Tooltip title="KRA Form Overview">
          <IconButton
            color="secondary"
            size="small"
            onClick={() =>
              navigate(`/manager/appraisal/kra_form_overview/${params.row.appraisalId}/${params.row.empId}`)
            }
          >
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const totalRows = filteredRows.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const paginatedRows = filteredRows.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* ── Compact Filter Bar ── */}
      <Card elevation={1} sx={{ mb: 1.5 }}>
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            px: 2,
            py: 1
          }}
        >
          <Typography
            sx={{
              fontFamily: properties.fontFamily,
              fontSize: "15px",
              fontWeight: "bold",
              whiteSpace: "nowrap"
            }}
          >
            KRA Status Report
          </Typography>

          <MDInput
            label="Employee ID"
            value={searchEmpId}
            onChange={e => setSearchEmpId(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            size="small"
            sx={{ flex: "1 1 150px", minWidth: "140px" }}
          />

          <FormControl size="small" sx={{ flex: "1 1 150px", minWidth: "140px" }}>
            <InputLabel id="employee-status-label">Employee Rating</InputLabel>
            <Select
              labelId="employee-status-label"
              value={employeeStatus}
              label="Employee Rating"
              onChange={e => setEmployeeStatus(e.target.value)}
              sx={{ fontFamily: properties.fontFamily, height: "36px" }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: "1 1 150px", minWidth: "140px" }}>
            <InputLabel id="manager-status-label">Manager Rating</InputLabel>
            <Select
              labelId="manager-status-label"
              value={managerStatus}
              label="Manager Rating"
              onChange={e => setManagerStatus(e.target.value)}
              sx={{ fontFamily: properties.fontFamily, height: "36px" }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>

          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSearch}
            sx={{
              minWidth: "90px",
              height: "36px",
              px: 2,
              background: properties.colors.bgcolorbluethem || "#1976d2",
              "&:hover": {
                background: properties.colors.bgcolorbluethem
                  ? `${properties.colors.bgcolorbluethem}ee`
                  : "#1565c0"
              }
            }}
          >
            Search
          </MDButton>

          <MDButton
            variant="outlined"
            color="info"
            onClick={handleReset}
            sx={{ minWidth: "90px", height: "36px", px: 2 }}
          >
            Reset
          </MDButton>
        </MDBox>
      </Card>

      {/* ── Data Table ── */}
      <Paper elevation={1}>
        <DataGrid
          rows={paginatedRows}
          columns={columns}
          loading={loading}
          hideFooterPagination
          disableRowSelectionOnClick
          autoHeight
          rowHeight={40}
          columnHeaderHeight={42}
          sx={{
            fontFamily: properties.fontFamily,
            fontSize: "13px",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
              fontWeight: "bold"
            },
            minHeight: "360px",
            "& .MuiDataGrid-scrollbar": { width: "8px !important" },
            "& .MuiDataGrid-cell": { py: 0 }
          }}
        />
        <CustomPagination
          page={page}
          pageSize={pageSize}
          totalRows={totalRows}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onGoToLastPage={handleGoToLastPage}
          loading={loading}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}